/**
 * AuthScreen component
 * Handles doctor and patient login/signup
 */

import React, { useState } from "react";
import { loginDoctor, loginPatient, signupDoctor, signupPatient } from "../services/auth";
import { isValidEmail, isValidPassword } from "../utils/helpers";
import { ErrorModal } from "./ui/ErrorModal";

export const AuthScreen = ({ onLogin }) => {
  const [mode, setMode] = useState("login");
  const [role, setRole] = useState("doctor");
  const [form, setForm] = useState({ name: "", email: "", password: "", doctorCode: "" });
  const [newDoctorCode, setNewDoctorCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const isDoctor = role === "doctor";

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleLogin = async () => {
    if (loading) return;
    setError("");

    if (!form.email || !form.password) {
      return setError("Please enter email and password.");
    }

    if (!isValidEmail(form.email)) {
      return setError("Please enter a valid email.");
    }

    setLoading(true);
    try {
      const user = role === "doctor" ? await loginDoctor(form.email, form.password) : await loginPatient(form.email, form.password);
      setLoading(false);
      onLogin(user);
    } catch (err) {
      setLoading(false);
      setError(err.message || "Login failed. Please try again.");
    }
  };

  const handleSignup = async () => {
    if (loading) return;
    setError("");

    if (!form.name || !form.email || !form.password) {
      return setError("Please fill all fields.");
    }

    if (!isValidEmail(form.email)) {
      return setError("Please enter a valid email.");
    }

    if (!isValidPassword(form.password)) {
      return setError("Password must be at least 6 characters.");
    }

    if (role === "patient" && !form.doctorCode) {
      return setError("Doctor code is required.");
    }

    setLoading(true);
    try {
      if (role === "doctor") {
        const user = await signupDoctor(form.name, form.email, form.password);
        setLoading(false);
        setNewDoctorCode(user.doctorCode);
      } else {
        const user = await signupPatient(form.name, form.email, form.password, form.doctorCode);
        setLoading(false);
        onLogin(user);
      }
    } catch (err) {
      setLoading(false);
      setError(err.message || "Signup failed. Please try again.");
    }
  };

  if (newDoctorCode) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4 py-8">
        <div className="w-full max-w-md rounded-3xl border border-sky-100 bg-white/90 p-6 shadow-xl shadow-cyan-100/60 backdrop-blur-sm sm:p-8">
          <div className="text-3xl font-semibold tracking-tight text-sky-700">MindBridge</div>
          <div className="mb-6 mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Account Created</div>
          <p className="mb-4 text-sm text-slate-500">
            Welcome! Share this code with your patients:
          </p>
          <div className="mb-4 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-center font-mono text-lg tracking-[0.2em] text-sky-700">{newDoctorCode}</div>
          <p className="mb-6 text-xs text-slate-500">
            Patients will use this code to link their account to you.
          </p>
          <button
            className="w-full rounded-xl bg-gradient-to-r from-sky-600 to-emerald-500 px-4 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:shadow-lg hover:shadow-sky-200 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
            onClick={() => {
              setNewDoctorCode("");
              setMode("login");
            }}
          >
            Continue to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4 py-8">
      <div className={`w-full max-w-md rounded-3xl border bg-white/90 p-6 shadow-xl backdrop-blur-sm sm:p-8 ${isDoctor ? "border-sky-100 shadow-cyan-100/60" : "border-emerald-100 shadow-emerald-100/60"}`}>
        <div className="text-3xl font-semibold tracking-tight text-sky-700">MindBridge</div>
        <div className="mb-6 mt-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Continuous mental health awareness</div>

        <div className={`mb-4 inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] ${isDoctor ? "border-sky-200 bg-sky-50 text-sky-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"}`}>
          {isDoctor ? "Doctor Mode" : "Patient Mode"}
        </div>

        <div className={`mb-4 grid grid-cols-2 gap-2 rounded-2xl border p-1 ${isDoctor ? "border-sky-100 bg-sky-50/70" : "border-emerald-100 bg-emerald-50/70"}`}>
          <button
            className={`rounded-xl px-3 py-2 text-sm font-semibold transition ${role === "doctor" ? "bg-sky-600 text-white shadow-sm" : "text-slate-500 hover:bg-slate-50 hover:text-slate-700"}`}
            onClick={() => setRole("doctor")}
          >
            Doctor
          </button>
          <button
            className={`rounded-xl px-3 py-2 text-sm font-semibold transition ${role === "patient" ? "bg-emerald-600 text-white shadow-sm" : "text-slate-500 hover:bg-slate-50 hover:text-slate-700"}`}
            onClick={() => setRole("patient")}
          >
            Patient
          </button>
        </div>

        <div className="mb-6 grid grid-cols-2 gap-2 rounded-2xl border border-sky-100 bg-white p-1">
          <button className={`rounded-xl px-3 py-2 text-sm font-semibold transition ${mode === "login" ? "bg-sky-100 text-sky-700" : "text-slate-500 hover:bg-slate-50 hover:text-slate-700"}`} onClick={() => setMode("login")}>
            Sign In
          </button>
          <button className={`rounded-xl px-3 py-2 text-sm font-semibold transition ${mode === "signup" ? "bg-sky-100 text-sky-700" : "text-slate-500 hover:bg-slate-50 hover:text-slate-700"}`} onClick={() => setMode("signup")}>
            Sign Up
          </button>
        </div>

        {mode === "signup" && (
          <div className="mb-4">
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Full Name</label>
            <input
              className="w-full rounded-xl border border-sky-100 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100"
              value={form.name}
              onChange={(e) => setF("name", e.target.value)}
              placeholder="Your full name"
            />
          </div>
        )}

        <div className="mb-4">
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Email</label>
          <input
            className="w-full rounded-xl border border-sky-100 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100"
            type="email"
            value={form.email}
            onChange={(e) => setF("email", e.target.value)}
            placeholder="you@email.com"
          />
        </div>

        <div className="mb-4">
          <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Password</label>
          <input
            className="w-full rounded-xl border border-sky-100 bg-white px-3.5 py-2.5 text-sm text-slate-800 outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100"
            type="password"
            value={form.password}
            onChange={(e) => setF("password", e.target.value)}
            placeholder="••••••••"
          />
        </div>

        {mode === "signup" && role === "patient" && (
          <div className="mb-4">
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">Doctor Code</label>
            <input
              className="w-full rounded-xl border border-sky-100 bg-white px-3.5 py-2.5 text-sm uppercase tracking-[0.1em] text-slate-800 outline-none transition focus:border-sky-300 focus:ring-4 focus:ring-sky-100"
              value={form.doctorCode}
              onChange={(e) => setF("doctorCode", e.target.value)}
              placeholder="e.g. SC7X2K"
            />
          </div>
        )}

        <button
          className="mt-2 w-full rounded-xl bg-gradient-to-r from-sky-600 to-emerald-500 px-4 py-3 text-sm font-semibold text-white transition hover:-translate-y-0.5 hover:shadow-lg hover:shadow-sky-200 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0"
          onClick={mode === "login" ? handleLogin : handleSignup}
          disabled={loading}
        >
          {loading ? "Please wait..." : mode === "login" ? "Sign In" : "Create Account"}
        </button>

        <ErrorModal message={error} onClose={() => setError("")} />

        
      </div>
    </div>
  );
};
